/**
 * Returns the correct dashboard path for a user based on their role,
 * facility type, and metadata. Used by Header nav + logo links.
 */
export function getDashboardPath(
  userProfile: Record<string, any> | null,
  userMetadata?: Record<string, any> | null
): string {
  if (!userProfile) return '/learn'

  const role = userProfile.facility_role
  const fType = userProfile.facility_type

  // Affiliate with no facility → affiliate dashboard
  if (userProfile.is_affiliate && !userProfile.facility_id) {
    return '/affiliates/dashboard'
  }

  // University student (role-flagged)
  if (role === 'university_student') {
    return '/university/dashboard'
  }

  // NGO contact (org_nominations link — set by invite-ngo for the org's admin contact)
  if (userProfile.is_ngo_contact || userMetadata?.is_ngo_contact) {
    return '/ngo/dashboard'
  }

  // ── Admin roles ────────────────────────────────────────────────────────
  if (role === 'admin') {
    // ── HARD GATE: platform orgs are a disjoint type — always the platform branch
    if (fType === 'platform') {
      return '/platform/setup'
    }
    if (fType === 'church') return '/church/admin'
    if (fType === 'university_ngo') return '/university/ngo/dashboard'
    if (fType === 'ngo') return '/ngo/dashboard'
    if (fType === 'company' || fType === 'corporate') return '/university/corporate/admin'
    if (fType === 'homeschool' || fType === 'family_edu') return '/homeschool/parent'
    if (fType === 'family') return '/family-admin'
    if (fType === 'school') return '/education-admin'
    if (fType === 'facility') return '/facility-admin'
    // Education admin via metadata
    if (userMetadata?.is_education_admin) return '/education-admin'
    if (userMetadata?.is_family_admin) return '/family-admin'
  }

  // ── Teacher ────────────────────────────────────────────────────────────
  if (role === 'teacher') return '/education-admin'

  // ── Member / student / resident roles ──────────────────────────────────
  if (role === 'member') {
    // ── HARD GATE (member): platform learners → /learn
    if (fType === 'platform') {
      return '/learn'
    }
    if (fType === 'church') return '/church/learn'
    // University NGO members and corporate employees learn; admins manage (handled above)
    if (fType === 'university_ngo' || fType === 'company' || fType === 'corporate') return '/university/dashboard'
    if (fType === 'ngo') return '/ngo/dashboard'
    // School students use the /learn student dashboard
    if (fType === 'school') return '/learn'
    // Homeschool and family_edu children use the /learn student dashboard
    if (fType === 'homeschool' || fType === 'family_edu' || fType === 'family') return '/learn'
    return '/learn'
  }

  if (role === 'student') return '/learn'
  if (role === 'resident') return '/church/learn'

  // ── Solo learners (no facility, no role) ───────────────────────────────
  const soloPlan: string = userProfile.preferences?.billing?.plan || userProfile.active_plan || ''

  // Solo education user with university access or university plan
  if (!userProfile.facility_id && (
    userProfile.university_access ||
    soloPlan.startsWith('university_') ||
    soloPlan.startsWith('scholar_pass_')
  )) {
    return '/university/dashboard'
  }

  // Solo learner with active education plan
  if (!userProfile.facility_id && ['homeschool_single', 'edu_elite', 'edu_bundle'].includes(soloPlan)) {
    return '/learn'
  }

  // Default companion dashboard
  return '/learn'
}